/**
 * TileEffectsPanel.tsx
 *
 * Sidescroller-scene corner panel showing card effects active on the current
 * tile + on its owner. Read-only — surfaces auras, pending PEQ entries, and
 * immunities so the active player can see WHY their next build/toll/destroy
 * may be modified.
 *
 * VISIBLE WHEN (two paths):
 *   1. hoveredTile !== null — hex-hover mode. Panel floats near cursor.
 *   2. turnPhase === "landed" && !buildMenuOpen — in-tile mode. Fixed corner.
 *
 * Feature added 2026-06-06 per user request.
 * Floating variant added 2026-06-08.
 */

import type React from "react";
import { useMemo, useState } from "react";
import { FACILITIES } from "../../store/facilities.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./TileEffectsPanel.module.css";

// Panel dimensions used for viewport clamping in floating mode.
const PANEL_W = 280;
const PANEL_H_EST = 520; // upper-bound for clamping (CSS max-height can reach ~vh-160); panel scrolls inside
const EDGE_PAD = 8; // min gap from any viewport edge

function facilityLabel(facilityId: string | undefined): string {
  if (!facilityId) return "Empty";
  const def = FACILITIES.find((f) => f.id === facilityId);
  return def?.name ?? facilityId;
}

function facilityCategory(facilityId: string | undefined): string | null {
  if (!facilityId) return null;
  const def = FACILITIES.find((f) => f.id === facilityId);
  return def?.category ?? null;
}

function _scopeIcon(scope: string): string {
  if (scope === "BuildCost") return "🔨";
  if (scope === "Energy") return "⚡";
  if (scope === "Pollution") return "💨";
  if (scope === "Toll") return "💰";
  return "🌀";
}

export function TileEffectsPanel(): React.ReactElement | null {
  const turnPhase = useGameStore((s) => s.turnPhase);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const builtFacilities = useGameStore((s) => s.builtFacilities);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const auraState = useGameStore((s) => s.auraState);
  const peqState = useGameStore((s) => s.peqState);
  const immunityState = useGameStore((s) => s.immunityState);
  const buildMenuOpen = useGameStore((s) => s.buildMenuOpen);
  const hoveredTile = useGameStore((s) => s.hoveredTile);
  const hoverPos = useGameStore((s) => s.hoverPos);

  const [collapsed, setCollapsed] = useState(false);

  // Floating positioning: cursor-anchored with viewport edge clamp.
  // Computed unconditionally (before early return) to satisfy hook rules.
  // Only applied when isHovering && hoverPos is present.
  const floatingStyle = useMemo((): React.CSSProperties | undefined => {
    if (hoverPos === null) return undefined;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const left = Math.max(EDGE_PAD, Math.min(hoverPos.x + 16, vw - PANEL_W - EDGE_PAD));
    const top = Math.max(EDGE_PAD, Math.min(hoverPos.y, vh - PANEL_H_EST - EDGE_PAD));
    return { left, top };
  }, [hoverPos]);

  // Two activation paths:
  //  1. Hex-hover on main board → show hoveredTile's effects (any phase)
  //  2. Inside sidescroller (turnPhase === "landed") → show current player's tile
  // Modal gates: hide when build menu is open over the tilestage.
  const isHovering = hoveredTile !== null;
  const isInTile = turnPhase === "landed" && !buildMenuOpen;
  if (!isHovering && !isInTile) return null;

  const tileIdx = isHovering ? hoveredTile : (playerTiles[currentSeat] ?? 0);
  const facilityAtTile = builtFacilities[tileIdx];
  const ownerSeat = ownedBy[tileIdx];

  // Compute which auras affect this tile.
  // - Auras owned by the tile's owner that match the facility category/type
  // - Auras owned by the active seat (could affect their next build on this tile)
  const relevantSeats = new Set<number>([currentSeat]);
  if (ownerSeat !== undefined) relevantSeats.add(ownerSeat);

  const cat = facilityCategory(facilityAtTile);
  const tileAuras: Array<{
    seat: number;
    sourceCardName: string;
    scope: string;
    valueInt: number;
    mult: number;
    add: number;
    categoryFilter: string | null;
  }> = [];
  for (const seat of relevantSeats) {
    const bucket = auraState?.bySeat?.[String(seat)] ?? [];
    for (const a of bucket) {
      const aCatFilter = a.categoryFilter ?? null;
      // Aura is relevant if:
      //  - it targets this seat AND
      //  - it has no category filter OR matches the facility category at this tile
      //    OR there's no facility at this tile (still relevant for next build)
      const matchesFacility =
        aCatFilter === null || facilityAtTile === undefined || cat === aCatFilter;
      if (matchesFacility) {
        tileAuras.push({
          seat,
          sourceCardName: a.sourceCardName,
          scope: a.scope,
          valueInt: a.effect.valueInt ?? 0,
          mult: a.effect.mult ?? 1,
          add: a.effect.add ?? 0,
          categoryFilter: aCatFilter,
        });
      }
    }
  }

  // Active-seat PEQ entries (next-build modifiers, halve-next-loss, etc.)
  const seatPEQ = peqState?.queues?.[String(currentSeat)] ?? [];

  // Active-seat immunities — flat entries list, filter by ownerSeat.
  const seatImmunities = (immunityState?.entries ?? []).filter(
    (im) => im.ownerSeat === currentSeat,
  );

  const totalEffects = tileAuras.length + seatPEQ.length + seatImmunities.length;

  const isFloating = isHovering && floatingStyle !== undefined;
  // Floating panel is pointer-events:none (can't be toggled), so always show its
  // body — otherwise a previously-collapsed in-tile panel would appear as a
  // dead, un-expandable collapsed sliver while hovering a hex.
  const showBody = !collapsed || isFloating;

  return (
    <div
      className={styles.panel}
      data-testid="tile-effects-panel"
      data-collapsed={collapsed && !isFloating}
      data-floating={isFloating ? "true" : undefined}
      style={isFloating ? floatingStyle : undefined}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={showBody}
        data-testid="tile-effects-toggle"
      >
        <span className={styles.title}>
          Tile #{tileIdx}{" "}
          {facilityLabel(facilityAtTile) !== "Empty" ? `· ${facilityLabel(facilityAtTile)}` : ""}
        </span>
        <span className={styles.count} data-testid="tile-effects-count">
          {totalEffects > 0
            ? `${totalEffects} effect${totalEffects === 1 ? "" : "s"}`
            : "no effects"}
        </span>
        <span className={styles.chevron} aria-hidden="true">
          {showBody ? "▾" : "▸"}
        </span>
      </button>

      {showBody && (
        <div className={styles.body}>
          {/* Tile facts */}
          <div className={styles.factRow}>
            <span className={styles.factLabel}>Owner:</span>
            <span className={styles.factValue}>
              {ownerSeat !== undefined ? `P${ownerSeat + 1}` : "Unowned"}
            </span>
          </div>

          {/* Auras */}
          {tileAuras.length > 0 && (
            <>
              <div className={styles.sectionHead}>Auras</div>
              <ul className={styles.list}>
                {tileAuras.map((a, i) => {
                  const sign = a.add > 0 ? "+" : "";
                  const multStr = a.mult !== 1 ? ` ×${a.mult}` : "";
                  const addStr = a.add !== 0 ? ` ${sign}${a.add}` : "";
                  return (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: stable list during render
                      key={`aura-${i}`}
                      className={styles.item}
                      data-testid={`tile-effects-aura-${i}`}
                    >
                      <span className={styles.icon}>{_scopeIcon(a.scope)}</span>
                      <span className={styles.itemMain}>
                        <span className={styles.cardName}>
                          {a.sourceCardName.replace(/_/g, " ")}
                        </span>
                        <span className={styles.itemDetail}>
                          {a.scope}
                          {a.categoryFilter ? ` · ${a.categoryFilter}` : ""}
                          {multStr}
                          {addStr}
                        </span>
                      </span>
                      <span className={styles.seatChip}>P{a.seat + 1}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {/* PEQ */}
          {seatPEQ.length > 0 && (
            <>
              <div className={styles.sectionHead}>Pending</div>
              <ul className={styles.list}>
                {seatPEQ.map((pe, i) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable list during render
                    key={`peq-${i}`}
                    className={styles.item}
                    data-testid={`tile-effects-peq-${i}`}
                  >
                    <span className={styles.icon}>⏳</span>
                    <span className={styles.itemMain}>
                      <span className={styles.cardName}>
                        {(pe.sourceCardName ?? "?").replace(/_/g, " ")}
                      </span>
                      <span className={styles.itemDetail}>
                        {pe.kind}
                        {pe.valueInt ? `: ${pe.valueInt > 0 ? "+" : ""}${pe.valueInt}` : ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* Immunity */}
          {seatImmunities.length > 0 && (
            <>
              <div className={styles.sectionHead}>Immunities</div>
              <ul className={styles.list}>
                {seatImmunities.map((im, i: number) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable list during render
                    key={`im-${i}`}
                    className={styles.item}
                    data-testid={`tile-effects-immunity-${i}`}
                  >
                    <span className={styles.icon}>🛡️</span>
                    <span className={styles.itemMain}>
                      <span className={styles.cardName}>
                        {im.sourceCardName.replace(/_/g, " ")}
                      </span>
                      <span className={styles.itemDetail}>
                        vs {im.againstKinds.join(", ")}
                        {im.tileIndex !== null ? ` · tile ${im.tileIndex}` : ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {totalEffects === 0 && (
            <div className={styles.empty} data-testid="tile-effects-empty">
              No active effects on this tile.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
